'use client'

import { ComponentProps, HTMLAttributes } from 'react'

import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'

import { PageCollectionViewHeroSectionFieldsFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useStaticAmplitudeTracking } from '../../../../_hooks/useStaticTracking'
import { TrackingLocationProvider } from '../../../../_providers/TrackingLocationProvider.client'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import PageHero from '../../../hero/PageHero/PageHero.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'

import { VideoDialogWrapper } from './ContentfulPageCollectionViewHeroSection.styles'

type ContentfulPageCollectionViewHeroSectionProps = {
  data: PageCollectionViewHeroSectionFieldsFragment
  additionalContent?: React.ReactNode
  pageSectionIndex: number
} & HTMLAttributes<HTMLDivElement>

export const ContentfulPageCollectionViewHeroSection = ({
  pageSectionIndex,
  additionalContent,
  data,
  ...rest
}: ContentfulPageCollectionViewHeroSectionProps) => {
  const translations = useTranslation()
  const section = data

  const {
    backgroundColor,
    textColor,
    title,
    subtitle,
    ctaLink,
    ctaText,
    imagePlacement,
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
  } = section

  const imageAsset =
    section.imageDesktop && section.imageMobile
      ? {
          srcFromMd: section.imageDesktop,
          srcUntilMd: section.imageMobile,
          isCritical: pageSectionIndex < 2,
        }
      : undefined

  const modalVideoAsset =
    section.videoId && section.videoPlatform
      ? {
          id: section.videoId,
          platform: section.videoPlatform,
        }
      : undefined

  return (
    <TrackingLocationProvider moduleName={section.name}>
      <PageHero.Wrapper
        type={section.__typename}
        id={section.id}
        data-testid='hero-section'
        $backgroundColor={backgroundColor}
        $desktopTextAlign={imageAsset ? 'left' : 'center'}
        $textColor={textColor}
        hasVerticalMargin={false}
        {...rest}
      >
        <PageHero.Content
          desktopWidth={imageAsset ? '50%' : '100%'}
          mobileWidth='100%'
          title={title && <PageHero.ContentTitle>{title}</PageHero.ContentTitle>}
          subtitle={subtitle && <PageHero.ContentSubtitle>{subtitle}</PageHero.ContentSubtitle>}
          cta={
            ctaLink &&
            ctaText && (
              <TrackingLocationProvider position={1}>
                <TrackedCta $variant={'white-on-black-no-border'} href={ctaLink}>
                  {ctaText}
                </TrackedCta>
              </TrackingLocationProvider>
            )
          }
        >
          {additionalContent ? <div>{additionalContent}</div> : null}
        </PageHero.Content>

        {imageAsset ? (
          <PageHero.Media
            placement={imagePlacement as 'right' | 'left'}
            image={
              <ResponsiveImage
                src={imageAsset.srcUntilMd}
                alt={title ?? ''}
                aspectRatios={{
                  sm: null,
                  md: null,
                }}
                loading={imageAsset.isCritical ? 'default' : 'lazy'}
                fetchPriority={imageAsset.isCritical ? 'high' : undefined}
                widths={{ unit: 'vw', sm: 100, md: 50 }}
                srcs={{
                  sm: imageAsset.srcUntilMd,
                  md: imageAsset.srcFromMd,
                }}
              />
            }
            video={
              <>
                <ClientOnlyResponsiveVideo
                  playText={translations.magnolia.general.playVideo}
                  pauseText={translations.magnolia.general.pauseVideo}
                  inlineVideoDesktop={inlineVideoDesktop}
                  inlineVideoMobile={inlineVideoMobile}
                  inlineVimeoIdDesktop={inlineVimeoIdDesktop}
                  inlineVimeoIdMobile={inlineVimeoIdMobile}
                />
                {modalVideoAsset ? (
                  <VideoDialogWrapper>
                    <WatchVideoDialog
                      videoId={modalVideoAsset.id}
                      videoPlatform={modalVideoAsset.platform === 'youtube' ? 'youtube' : 'vimeo'}
                    />
                  </VideoDialogWrapper>
                ) : null}
              </>
            }
          />
        ) : null}
      </PageHero.Wrapper>
    </TrackingLocationProvider>
  )
}

// Its own component so the event reads the CTA's TrackingLocationProvider.
const TrackedCta = (props: ComponentProps<typeof PageHero.Cta> & { href: string }) => {
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  return (
    <PageHero.Cta
      {...props}
      forwardedAs={NextLink}
      onClick={() =>
        trackStaticAmplitudeEvent({ modifiedElement: 'Module Link', href: props.href })
      }
    />
  )
}
